import React, { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import clsx from 'clsx';
import { FileRejection, useDropzone } from 'react-dropzone';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';

import { useSelector } from 'store';
import { useTranslation } from 'hooks/useTranslation';
import { Paper } from 'features/app/components/Paper';
import { bulkEmailValidationRequest } from 'features/dashboard/redux/actions';
import { useMessage } from 'features/message/hooks/useMessage';

import { ReactComponent as UploadProgressIcon } from 'assets/icons/upload-progress-icon.svg';

import { useStyles } from './styles';

interface BulkUploadSectionProp {
  onSelectFile: (fileName: string) => void;
}

export const BulkUploadSection = ({ onSelectFile }: BulkUploadSectionProp) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const { t } = useTranslation('dashboard');
  const { showErrorMessage } = useMessage();
  const ordersState = useSelector(state => state.dashboard.orders);
  const isDropzoneDisabled = useMemo(() => ordersState?.isLoading || ordersState?.isBulkUploaded, [ordersState]);

  const handleDrop = useCallback(
    <T extends File>(acceptedFiles: T[], fileRejections: FileRejection[]) => {
      if (fileRejections?.length > 0) {
        showErrorMessage(t('invalidFileFormat'));
      } else {
        onSelectFile(acceptedFiles[0].name);
        const formData = new FormData();
        formData.append('file', acceptedFiles[0]);
        dispatch(bulkEmailValidationRequest(formData));
      }
    },
    [dispatch, onSelectFile, showErrorMessage, t]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    multiple: false,
    disabled: isDropzoneDisabled,
    maxFiles: 1,
    accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel',
  });

  return (
    <Paper borderRadius={20} className={classes.bulkUploadContainer}>
      <Box flex={1} display="flex" justifyContent="center">
        <Box
          flex={1}
          display="flex"
          flexDirection="column"
          className={classes.bulkUploadContent}
          paddingTop={ordersState?.isBulkUploaded ? 0 : 57.5}
        >
          <Box
            {...getRootProps()}
            display="flex"
            alignItems="center"
            justifyContent="center"
            paddingX={24}
            className={clsx(classes.dropzone, {
              [classes.hoverableDropzone]: !isDropzoneDisabled,
              [classes.activeDropzone]:
                isDragActive && (!ordersState?.isLoading || !ordersState?.isBulkEmailValidationLoading),
            })}
          >
            {ordersState?.isLoading || ordersState?.isBulkEmailValidationLoading ? (
              <Box display={'flex'} flexDirection={'column'} justifyContent={'center'} alignItems={'center'}>
                <Box>
                  <CircularProgress size={60} />
                </Box>
                <Box className={classes.loadingMsg} mt={10}>
                  <Box component={'span'}>
                    {ordersState?.isLoading ? t('summarizingOrder') : t('emailsVerification')}
                  </Box>
                  <Box component={'span'} className={classes.threeDots} />
                </Box>
              </Box>
            ) : (
              <>
                <input
                  {...getInputProps()}
                  type="file"
                  multiple={false}
                  accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                />
                <UploadProgressIcon />
                <Box paddingLeft={15} paddingRight={15}>
                  <Typography className={classes.dropzoneTitle} variant="h3">
                    {t('dropYourFileHere')}
                  </Typography>
                  <Typography className={classes.dropzoneSubtitle} color="textSecondary">
                    {t('orSelectFromFolder')}
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};
